import _objectWithoutProperties from "/ecomm-marketplace/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["id"];
import _ from 'lodash';
// eslint-disable-next-line import/no-extraneous-dependencies
import Big from 'big.js';
import moment from 'moment-timezone';
import { isBlank } from 'underscore.string';
import { remove as removeDiacritics } from 'diacritics';
import { formatName } from 'shared/helpers/users';
import { integrationDisplayName } from 'shared/helpers/integrations';
import { defaultDurationEstimates, archiveReasons, paymentMethodsToDisplayNamesAdmin, CURBSIDE_PICKUP_SPECIAL_INSTRUCTIONS, DRIVE_THRU_PICKUP_SPECIAL_INSTRUCTIONS, SPECIAL_INSTRUCTIONS_MAX_LENGTH } from 'shared/constants';
import { getDefaultRewardsProgramDisplayName } from 'shared/core/helpers/dispensaries';
import { hasNonCashPaymentOptions } from './dispensaries';
import { formatTimeRange } from './time-utils';
export var getCustomerEmail = function getCustomerEmail(order) {
  var _order$guestCustomer;
  var emailAddress = _.get(order, 'customer.profile.email', null);
  if (emailAddress) {
    return emailAddress;
  }
  emailAddress = _.get(order, 'customer.emails[0].address', null);
  if (emailAddress) {
    return emailAddress;
  }
  if ((_order$guestCustomer = order.guestCustomer) !== null && _order$guestCustomer !== void 0 && _order$guestCustomer.email) {
    return order.guestCustomer.email;
  }
  return null;
};
export var getCustomerNameForOrder = function getCustomerNameForOrder(order) {
  var _order$customer;
  if ((_order$customer = order.customer) !== null && _order$customer !== void 0 && _order$customer.profile) {
    var _order$customer2;
    return formatName((_order$customer2 = order.customer) === null || _order$customer2 === void 0 ? void 0 : _order$customer2.profile);
  }
  if (order.guestCustomer) {
    return formatName(order.guestCustomer);
  }
  return 'unknown';
};
export var getCustomerPhoneForOrder = function getCustomerPhoneForOrder(order) {
  var _order$customer3, _order$guestCustomer2;
  if ((_order$customer3 = order.customer) !== null && _order$customer3 !== void 0 && (_order$customer3 = _order$customer3.profile) !== null && _order$customer3 !== void 0 && _order$customer3.phone) {
    return order.customer.profile.phone;
  }
  if ((_order$guestCustomer2 = order.guestCustomer) !== null && _order$guestCustomer2 !== void 0 && _order$guestCustomer2.phone) {
    return order.guestCustomer.phone;
  }
  return 'Unknown';
};
export var getOrderDateAndTime = function getOrderDateAndTime(order) {
  var _order$dispensary;
  var timezone = order.dispoTimezone || ((_order$dispensary = order.dispensary) === null || _order$dispensary === void 0 ? void 0 : _order$dispensary.timezone) || "America/Los_Angeles";
  var orderTime;
  if (_.isDate(order.createdAt)) {
    orderTime = moment.tz(order.createdAt, timezone);
  } else {
    orderTime = moment.tz(parseInt(order.createdAt, 10), timezone);
  }
  var date = orderTime.format("M/DD/YYYY");
  var time = orderTime.format("h:mm A");
  return "".concat(date, " at ").concat(time);
};

/**
 * Formats an in-store (POS) order date for display.
 *
 * Accepts a local ISO 8601 string (e.g. "2026-03-20T18:21:56.383") where the time
 * is already in the dispensary's local timezone. No timezone conversion is applied.
 *
 * @param {string | null | undefined} isoString - The local ISO date string from shipmentDeliveredLocal
 * @returns {string | null} Formatted date string (e.g. "3/20/2026 at 6:21 PM"), or null if not provided or invalid
 */
/**
 * Formats an in-store (POS) order address for display.
 *
 * @param {{ street?: string | null, city?: string | null, state?: string | null }} locationDetails
 * @returns {string | null} Formatted address (e.g. "123 Main St, Portland, OR"), or null if any field is missing
 */
export var getInstoreOrderAddress = function getInstoreOrderAddress() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    street = _ref.street,
    city = _ref.city,
    state = _ref.state;
  var hasValidLocationDetails = !!street && !!city && !!state;
  return hasValidLocationDetails ? [street, city, state].join(', ') : null;
};
export var getInstoreOrderDateAndTime = function getInstoreOrderDateAndTime(isoString) {
  if (!isoString) {
    return null;
  }
  var orderTime = moment(isoString, moment.ISO_8601);
  if (!orderTime.isValid()) {
    return null;
  }
  var date = orderTime.format("M/DD/YYYY");
  var time = orderTime.format("h:mm A");
  return "".concat(date, " at ").concat(time);
};
export var getDurationDisplayValue = function getDurationDisplayValue(durationMinutes) {
  var shortened = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  if (!_.isFinite(durationMinutes)) {
    return '-';
  }
  if (durationMinutes === 0 || durationMinutes < 2) {
    return shortened ? '1m' : '1 min';
  }
  if (durationMinutes > 60) {
    return "".concat(moment.duration(durationMinutes, 'minutes').hours(), "h ").concat(durationMinutes % 60).concat(shortened ? 'm' : 'mins');
  }
  return "".concat(durationMinutes).concat(shortened ? ' m' : ' mins');
};
export var getTimestampInfo = function getTimestampInfo(order) {
  var _order$dispensary2;
  var timezone = order.dispoTimezone || ((_order$dispensary2 = order.dispensary) === null || _order$dispensary2 === void 0 ? void 0 : _order$dispensary2.timezone) || 'America/Los_Angeles';
  var orderTime = moment.tz(order.createdAt, timezone);
  var timeDisplay;
  if (moment().diff(orderTime, 'minutes') < 1) {
    timeDisplay = "".concat(moment().diff(orderTime, 'seconds'), " seconds ago");
    if (timeDisplay.charAt(0) === '-') {
      timeDisplay = 'just now';
    }
  } else {
    var duration = moment.duration(moment().diff(orderTime, 'minutes'), 'minutes');
    if (duration.years() > 0 || duration.months() > 0) {
      timeDisplay = "> ".concat(duration.humanize());
    } else if (duration.days() > 0) {
      timeDisplay = "".concat(duration.days(), "d ").concat(duration.hours(), "h ").concat(duration.minutes(), "m ago");
    } else if (duration.hours() > 0) {
      timeDisplay = "".concat(duration.hours(), "h ").concat(duration.minutes(), "m ago");
    } else {
      timeDisplay = "".concat(duration.minutes(), " minutes ago");
    }
  }
  var estimate;
  if (order.durationEstimates) {
    estimate = order.durationEstimates[order.deliveryInfo.deliveryOption ? 'delivery' : 'pickup'];
  }
  if (!estimate) {
    estimate = defaultDurationEstimates[order.deliveryInfo.deliveryOption ? 'delivery' : 'pickup'];
  }
  var estimateCeiling = orderTime.clone().add(estimate.highInMinutes, 'minutes');
  return {
    timeDisplay: timeDisplay,
    isLate: estimateCeiling.isBefore(moment())
  };
};
export var getProductNameForLineItem = function getProductNameForLineItem(item, dispensary) {
  var _dispensary$ordersCon;
  var product = item.product,
    _item$option = item.option,
    option = _item$option === void 0 ? 'N/A' : _item$option;
  if (dispensary !== null && dispensary !== void 0 && (_dispensary$ordersCon = dispensary.ordersConfig) !== null && _dispensary$ordersCon !== void 0 && _dispensary$ordersCon.posItemNames) {
    var _product$POSMetaData, _product$POSMetaData2;
    if ((_product$POSMetaData = product.POSMetaData) !== null && _product$POSMetaData !== void 0 && (_product$POSMetaData = _product$POSMetaData.children) !== null && _product$POSMetaData !== void 0 && _product$POSMetaData.length) {
      var childProduct = _.find(product.POSMetaData.children, {
        option: option
      });
      if (childProduct && !isBlank(childProduct.canonicalName)) {
        return childProduct.canonicalName;
      }
    }
    if (!isBlank((_product$POSMetaData2 = product.POSMetaData) === null || _product$POSMetaData2 === void 0 ? void 0 : _product$POSMetaData2.canonicalName)) {
      return product.POSMetaData.canonicalName;
    }
    if (!isBlank(product.integrationKey)) {
      return product.integrationKey;
    }
  }
  return product.Name || '';
};
export var getMedCardString = function getMedCardString(order) {
  var medCard = order.medicalCard || _.get(order, 'customer.profile.medicalCard', null);
  if (!medCard) {
    var orderType = order.deliveryInfo.deliveryOption ? 'delivery' : 'pickup';
    return "Will present upon ".concat(orderType);
  }
  var number = medCard.number;
  var expirationDate = medCard === null || medCard === void 0 ? void 0 : medCard.expirationDate;
  if (expirationDate) {
    return "".concat(number, " - Exp: ").concat(expirationDate);
  }
  return '';
};
export var getStatusText = function getStatusText(order) {
  var reopened = order.reopened,
    status = order.status;
  var suffix = reopened ? ' (reopened)' : '';
  if (status === 'inTransit') {
    if (!order.deliveryInfo.deliveryOption) {
      return 'Awaiting Pickup';
    }
    return "In Transit".concat(suffix);
  }
  if (status === 'confirmed') {
    return "Confirmed".concat(suffix);
  }
  if (status === 'closed') {
    return 'Closed';
  }
  if (status === 'open') {
    return 'New';
  }
  return '';
};
export var getCustomerDOB = function getCustomerDOB(order) {
  if (order.isGuestOrder) {
    var _order$guestCustomer3 = order.guestCustomer,
      birthMonth = _order$guestCustomer3.birthMonth,
      birthDay = _order$guestCustomer3.birthDay,
      birthYear = _order$guestCustomer3.birthYear;
    if (birthMonth && birthDay && birthYear) {
      return "".concat(birthMonth, "/").concat(birthDay, "/").concat(birthYear);
    }
    return null;
  }
  return order.customer.profile.birthday;
};
export var getCustomerBirthdayForOrder = function getCustomerBirthdayForOrder(order) {
  var _order$customer4;
  if ((_order$customer4 = order.customer) !== null && _order$customer4 !== void 0 && (_order$customer4 = _order$customer4.profile) !== null && _order$customer4 !== void 0 && _order$customer4.birthday) {
    return moment(order.customer.profile.birthday, 'M-D-YYYY');
  }
  if (order.guestCustomer) {
    var guestCustomer = order.guestCustomer;
    return moment("".concat(guestCustomer.birthMonth, "-").concat(guestCustomer.birthDay, "-").concat(guestCustomer.birthYear), 'M-D-YYYY');
  }
  return null;
};
export var getCustomerIdForOrder = function getCustomerIdForOrder(order) {
  var _order$customer5, _order$guestCustomer4;
  if ((_order$customer5 = order.customer) !== null && _order$customer5 !== void 0 && (_order$customer5 = _order$customer5.profile) !== null && _order$customer5 !== void 0 && _order$customer5.photoId) {
    return order.customer.profile.photoId;
  }
  if ((_order$guestCustomer4 = order.guestCustomer) !== null && _order$guestCustomer4 !== void 0 && _order$guestCustomer4.photoId) {
    return order.guestCustomer.photoId;
  }
  return null;
};
export var getCustomerDriversLicenceForOrder = function getCustomerDriversLicenceForOrder(order) {
  var _order$customer6;
  if ((_order$customer6 = order.customer) !== null && _order$customer6 !== void 0 && (_order$customer6 = _order$customer6.profile) !== null && _order$customer6 !== void 0 && _order$customer6.driversLicense) {
    return order.customer.profile.driversLicense;
  }
  if (order.driversLicense) {
    return order.driversLicense;
  }
  return null;
};
export var getCustomerInfoFieldsForOrder = function getCustomerInfoFieldsForOrder(order) {
  var customerName = getCustomerNameForOrder(order);
  var customerPhone = getCustomerPhoneForOrder(order);
  var customerEmail = getCustomerEmail(order);
  var customerId = getCustomerIdForOrder(order);
  var customerBirthday = getCustomerBirthdayForOrder(order);
  var customerDriversLicense = getCustomerDriversLicenceForOrder(order);
  return {
    customerName: customerName,
    customerPhone: customerPhone,
    customerEmail: customerEmail,
    customerId: customerId,
    customerBirthday: customerBirthday,
    customerDriversLicense: customerDriversLicense
  };
};
/** @typedef {{ id: string | null, reason: string | null, customerMessage: string | null }} ArchiveReason  */
/**
 * @returns {ArchiveReason}
 */
export var getArchiveReason = function getArchiveReason(_ref2) {
  var id = _ref2.id,
    props = _objectWithoutProperties(_ref2, _excluded);
  return _.find(archiveReasons(props), {
    id: id
  }) || {
    id: null,
    reason: null,
    customerMessage: null
  };
};
export var getUserContext = function getUserContext(context) {
  var by = context.user._id || context.user.deviceId;
  var byType = context.user._id ? 'user' : 'device';
  var agentName = context.user.agentName;
  return {
    by: by,
    byType: byType,
    agentName: agentName
  };
};
export var getScheduledOrderTime = function getScheduledOrderTime(_ref3) {
  var reservation = _ref3.reservation,
    timezone = _ref3.timezone;
  if (!(reservation !== null && reservation !== void 0 && reservation.startTimeISO)) {
    return false;
  }
  var startTimeISO = reservation.startTimeISO,
    endTimeISO = reservation.endTimeISO,
    dayOfWeek = reservation.dayOfWeek,
    isNextDaySlot = reservation.isNextDaySlot;
  var startTimeMoment = moment.tz(startTimeISO, timezone);
  var endTimeMoment = moment.tz(endTimeISO, timezone);

  // Use the effective business day from the slot instead of calculating it
  var nowInDispensaryTimezone = moment.tz(timezone);
  var todayDayOfWeek = nowInDispensaryTimezone.format('dddd');
  var tomorrowDayOfWeek = nowInDispensaryTimezone.clone().add(1, 'day').format('dddd');
  var prefix;
  if (dayOfWeek === todayDayOfWeek) {
    prefix = 'Today';
  } else if (dayOfWeek === tomorrowDayOfWeek) {
    prefix = 'Tomorrow';
  } else {
    // For slots beyond tomorrow, show the date
    prefix = startTimeMoment.format('M/D');
  }
  var _formatTimeRange = formatTimeRange(startTimeMoment, endTimeMoment, {
      showMinutes: true,
      nextDayIndicator: '(next day)',
      format: 'h:mma',
      isNextDaySlot: isNextDaySlot
    }),
    start = _formatTimeRange.start,
    end = _formatTimeRange.end;
  return {
    dayOfWeek: dayOfWeek,
    prefix: prefix,
    startTime: start,
    endTime: end
  };
};
export var formatReward = function formatReward(reward, _ref4) {
  var _storeSettings$reward, _storeSettings$reward2;
  var storeSettings = _ref4.storeSettings,
    location = _ref4.location;
  if (!reward) {
    return '';
  }
  var copy = reward.copy,
    operator = reward.operator,
    value = reward.value;
  var rewardPrefix = (_storeSettings$reward = storeSettings === null || storeSettings === void 0 || (_storeSettings$reward2 = storeSettings.rewardsIntegrationConfiguration) === null || _storeSettings$reward2 === void 0 ? void 0 : _storeSettings$reward2.rewardsProgramDisplayName) !== null && _storeSettings$reward !== void 0 ? _storeSettings$reward : getDefaultRewardsProgramDisplayName({
    location: location
  });
  var dollarText = operator === '$' ? operator : '';
  var percentText = operator === '%' ? operator : '';

  // Catch everything that is non-numeric, 0, or falsey
  rewardPrefix += !+value ? '' : " ".concat(dollarText).concat(value).concat(percentText);

  // TODO: add "for x pts" at the end here, if available (not currently in data)
  return "".concat(rewardPrefix, " discount applied (").concat(copy, ")");
};
export var formatRewardStatus = function formatRewardStatus(reward, showRewardStatus) {
  if (!reward || !showRewardStatus) {
    return '';
  }
  var redemptionAttempts = reward.redemptionAttempts;
  var _ref5 = _.head(redemptionAttempts) || {},
    status = _ref5.status,
    redeemBy = _ref5.redeemBy;
  if (!status || !redeemBy) {
    return '';
  }
  var redeemStatus = status === 'pending' ? '- Redeemable at' : 'by';
  return "Reward ".concat(_.capitalize(status), " ").concat(redeemStatus, " ").concat(_.upperCase(redeemBy));
};
export var getFormattedRewardData = function getFormattedRewardData(_ref6, showRewardStatus, dispensary) {
  var appliedRewards = _ref6.appliedRewards;
  var reward = _.head(appliedRewards);
  var formattedReward = formatReward(reward, dispensary);
  var formattedRewardStatus = formatRewardStatus(reward, showRewardStatus);
  return {
    formattedReward: formattedReward,
    formattedRewardStatus: formattedRewardStatus
  };
};
export var cleanStringForPrinter = function cleanStringForPrinter(str) {
  if (!str) {
    return '';
  }
  try {
    // replace accented characters with the most similar ascii character
    var cleanedString = removeDiacritics(str);

    // replace common fraction characters with ascii
    cleanedString = _.replace(cleanedString, /¼/g, ' 1/4');
    cleanedString = _.replace(cleanedString, /½/g, ' 1/2');
    cleanedString = _.replace(cleanedString, /¾/g, ' 3/4');

    // remove any remaining non-ascii
    // eslint-disable-next-line
    cleanedString = _.replace(cleanedString, /[^\x00-\x7F]/g, '');
    return cleanedString;
  } catch (e) {
    console.error(e);
    return '';
  }
};
export var getSpecialInstructions = function getSpecialInstructions(order, curbsideArrivalInfo, showRewardStatus, dispensary) {
  var curbsideMessage = "".concat(CURBSIDE_PICKUP_SPECIAL_INSTRUCTIONS, " ").concat(cleanStringForPrinter(curbsideArrivalInfo === null || curbsideArrivalInfo === void 0 ? void 0 : curbsideArrivalInfo.arrivalInformation), " ");
  var driveThruMessage = "".concat(DRIVE_THRU_PICKUP_SPECIAL_INSTRUCTIONS, " ");
  var _getFormattedRewardDa = getFormattedRewardData(order, showRewardStatus, dispensary),
    formattedReward = _getFormattedRewardDa.formattedReward,
    formattedRewardStatus = _getFormattedRewardDa.formattedRewardStatus;
  var specialInstructions = "".concat(cleanStringForPrinter(order.specialInstructions));
  if (!isBlank(formattedRewardStatus)) {
    specialInstructions = formattedRewardStatus.concat(specialInstructions);
  }
  if (!isBlank(formattedReward)) {
    specialInstructions = "".concat(formattedReward, " ").concat(specialInstructions);
  }
  if (order.isCurbsidePickupOrder) {
    specialInstructions = curbsideMessage.concat(specialInstructions);
  }
  if (order.isDriveThruPickupOrder) {
    specialInstructions = driveThruMessage.concat(specialInstructions);
  }
  return specialInstructions.substring(0, SPECIAL_INSTRUCTIONS_MAX_LENGTH);
};
export var formatPaymentMethod = function formatPaymentMethod(_ref7, dispensary) {
  var card = _ref7.card,
    paymentMethod = _ref7.paymentMethod;
  var debitOrder = !dispensary.paymentTypesAccepted.creditCardAtDoor && !dispensary.paymentTypesAccepted.creditCardByPhone && !dispensary.paymentTypesAccepted.linx && dispensary.paymentTypesAccepted.debit; // eslint-disable-line

  // old format payment info
  if (card) {
    return card === 'cash' ? 'Cash' : "".concat(debitOrder ? 'Debit' : 'Credit', " Card");
  }
  var method = _.get(paymentMethodsToDisplayNamesAdmin, paymentMethod, paymentMethod);
  if (method === 'Credit Card' && debitOrder) {
    method = 'Debit Card';
  }
  return method;
};
var acceptsDetailedPaymentTypes = function acceptsDetailedPaymentTypes(dispensary) {
  return dispensary.paymentTypesAccepted.payOnlineMerrco || dispensary.paymentTypesAccepted.payOnlineMoneris || dispensary.paymentTypesAccepted.payOnlineHypur || dispensary.paymentTypesAccepted.dutchiePay || dispensary.paymentTypesAccepted.payOnlineChase || dispensary.paymentTypesAccepted.rethinkPay;
};
var hasDetailedPaymentInfo = function hasDetailedPaymentInfo(order) {
  return order && (!_.isEmpty(order.paysafe) || !_.isEmpty(order.hypur) || !_.isEmpty(order.moneris));
};
export var showFullPaymentOptions = function showFullPaymentOptions(_ref8, dispensary) {
  var isDelivery = _ref8.isDelivery,
    order = _ref8.order;
  return isDelivery && hasNonCashPaymentOptions(dispensary) || hasDetailedPaymentInfo(order) || acceptsDetailedPaymentTypes(dispensary) || hasNonCashPaymentOptions(dispensary) && !_.isEmpty(dispensary.paymentFees);
};
export var getOrderSyncFailureText = function getOrderSyncFailureText(_ref9) {
  var pos = _ref9.pos;
  var posName = integrationDisplayName(pos.name);
  return "this order couldn't be sent to ".concat(posName, ".").concat(!isBlank(pos === null || pos === void 0 ? void 0 : pos.humanReadableError) ? " ".concat(pos.humanReadableError, " ") : ' ', "The order must be entered manually into ").concat(posName, ".");
};
export var isWithinThreshold = function isWithinThreshold(_ref10) {
  var _ref10$actual = _ref10.actual,
    actual = _ref10$actual === void 0 ? 0 : _ref10$actual,
    _ref10$expected = _ref10.expected,
    expected = _ref10$expected === void 0 ? 0 : _ref10$expected,
    _ref10$threshold = _ref10.threshold,
    threshold = _ref10$threshold === void 0 ? 0 : _ref10$threshold;
  var diff = Big(Big(expected).minus(actual)).abs();
  return diff.lte(Big(threshold));
};
export var willSendOrderReadySMS = function willSendOrderReadySMS(dispensary, order, textNotifications) {
  var deliveryInfo = order.deliveryInfo,
    guestCustomer = order.guestCustomer;
  var messagingSettings = dispensary.messagingSettings;
  var disableReadyForPickup = messagingSettings.disableReadyForPickup,
    disableStartDelivery = messagingSettings.disableStartDelivery;
  var serviceType = deliveryInfo !== null && deliveryInfo !== void 0 && deliveryInfo.deliveryOption ? "delivery" : "pickup";
  var customerCanBeNotified = textNotifications || (guestCustomer === null || guestCustomer === void 0 ? void 0 : guestCustomer.phone);
  var willSendPickupText = customerCanBeNotified && !disableReadyForPickup && serviceType !== 'delivery';
  var willSendDeliveryText = customerCanBeNotified && !disableStartDelivery && serviceType === 'delivery';
  return willSendPickupText || willSendDeliveryText;
};